import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import './Toast.css';

const ToastContext = createContext({
  showToast: () => {},
});

let toastId = 0;

export const ToastProvider = ({ children, position = 'bottom-right', maxToasts = 3 }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const removeToast = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    (message, type = 'info') => {
      if (!message) return;
      const id = ++toastId;
      setToasts((current) => {
        const next = [...current, { id, message, type }];
        // limit stack to maxToasts, remove oldest
        if (next.length > maxToasts) {
          const [oldest, ...rest] = next;
          removeToast(oldest.id);
          return rest;
        }
        return next;
      });

      const timer = setTimeout(() => removeToast(id), 3500);
      timers.current.set(id, timer);
    },
    [maxToasts, removeToast]
  );

  useEffect(
    () => () => {
      timers.current.forEach((timer) => clearTimeout(timer));
      timers.current.clear();
    },
    []
  );

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={`toast-container ${position}`} role="status" aria-live="polite" aria-atomic="true">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast ${toast.type}`}>
            <div className="toast-message">{toast.message}</div>
            <button
              className="toast-close"
              aria-label="Close notification"
              onClick={() => removeToast(toast.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
export default ToastContext;

